import { ThemeEvents } from '@theme/events';
import { nostoLogger, syncNostoCart } from '@theme/nosto-placements';

/**
 * Keeps Nosto's cart tagging in step with the cart.
 *
 * The Nosto app embed renders the cart tagging server-side, so it only ever describes the cart as it
 * was when the page loaded. Every mutation after that (add, quantity change, removal) happens in
 * AJAX and leaves Nosto with a stale cart, which Lucie Leonhardt confirmed on 2026-08-12 while
 * looking at the account. That matters beyond reporting: excluding products already in the cart from
 * the cart recommendation is only possible if the tagging is current.
 *
 * No request of its own: both mutation paths already hold the new cart when they announce it
 * (`product-form` fetches `/cart.js`, `cart-items` gets the cart back from `/cart/change.js`), so
 * they pass it along in the event and this listener only has to forward it.
 *
 * Registered in the **capture** phase so it runs before the components that listen to the same event
 * on `document` — chiefly `cart-confirmation-cross-sell`, whose placement reload then carries the
 * queued cart in its own request rather than racing it.
 *
 * Debug: `?nostodebug=true` logs each push.
 */

const debug = nostoLogger('nosto-cart-tagging');

document.addEventListener(
  ThemeEvents.cartUpdate,
  (event) => {
    if (!(event instanceof CustomEvent)) return;

    const cart = event.detail?.data?.cart;
    if (!cart || typeof cart !== 'object') {
      debug('Cart update carried no cart state — tagging left as is', event.detail?.data?.source);
      return;
    }

    syncNostoCart(/** @type {ShopifyCart} */ (cart), debug);
  },
  { capture: true }
);
